<template>
    <sui-segment :loading="isLoading" clearing>
        <div class="segment-header">
            <h1 is="sui-header">{{
                    $t('override.form.title', {gateway_name: gateway.name, customer_name: customer.name})
                }}</h1>
            <sui-button @click="saveOverride" :content="$t('override.form.save')" color="violet"/>
        </div>
        <div class="segment-header">
            <sui-button floated="left" @click="displayCode" :content="$t('bindings.displayCode')"/>
        </div>

        <sui-table color="violet" selectable celled v-for="(value, name) in bindings" :key="name">
            <sui-table-header>
                <sui-table-row>
                    <sui-table-headerCell class="cursorPointer" colspan="4" v-on:click="collapse(name)">
                        <sui-icon name="sort"/>
                        <span v-if="isDisplayCode">{{ name }}</span>
                        <span v-else>{{ $t(`${name}._title`) }}</span>
                    </sui-table-headerCell>
                </sui-table-row>
                <sui-table-row :class="name">
                    <sui-table-header-cell :width="1">{{ $t('bindings.form.dataToMap') }}</sui-table-header-cell>
                    <sui-table-header-cell :width="2">{{ $t('bindings.form.relatedField') }}</sui-table-header-cell>
                    <sui-table-header-cell :width="2">{{ $t('override.form.overridedField') }}</sui-table-header-cell>
                    <sui-table-header-cell :width="11">
                        <sui-form>
                            Mandat d'exemple
                            <sui-form-fields fields="two">
                                <sui-form-field>
                                    <label>{{ $t('common.gateway') }}</label>
                                    <sui-dropdown
                                            :options="gatewayFilesOptions"
                                            search
                                            selection
                                            v-model="gatewayFileSelected"
                                            @input="gatewayFileUpdated"
                                    />
                                </sui-form-field>

                                <sui-form-field>
                                    <label>{{ $t('common.mandate') }}</label>
                                    <sui-dropdown
                                            :options="listingsOptions"
                                            search
                                            selection
                                            v-model="listingSelected"
                                            @input="listingUpdated"
                                    />
                                </sui-form-field>
                            </sui-form-fields>
                        </sui-form>
                    </sui-table-header-cell>
                </sui-table-row>
            </sui-table-header>

            <sui-table-body :class="name">
                <sui-table-row v-for="binding of value" :key="binding.static_binding.id">
                    <sui-table-cell>
                        <span v-if="isDisplayCode">{{ binding.static_binding.code }}</span>
                        <span v-else>{{ binding.label }}</span>
                    </sui-table-cell>
                    <sui-table-cell>
                        {{ binding.field }}
                    </sui-table-cell>
                    <sui-table-cell>
                        <sui-input
                                size="mini"
                                fluid
                                v-model="binding.override"
                        />
                    </sui-table-cell>
                    <sui-table-cell style="word-break: break-all">
                        {{ getListingExampleValue(binding.field, binding.override) }}
                    </sui-table-cell>
                </sui-table-row>
            </sui-table-body>
        </sui-table>
    </sui-segment>
</template>

<script>
import {api} from '@/api';
import jsonpath from 'jsonpath';
import {regroupBindings} from "@/helper/regroupBindings";

export default {
    name: 'CustomerGatewayOverrideForm',
    data() {
        return {
            isLoading: false,
            gatewayId: this.$route.params.gateway_id,
            customerId: this.$route.params.customer_id,
            gateway: {
                id: null,
                name: null,
            },
            customer: {
                id: null,
                name: null,
            },
            listing: {
                id: null,
                content: null,
                content_link: null,
            },
            bindings: [],

            gatewayFilesOptions: [],
            gatewayFileSelected: null,

            listingsOptions: [],
            listingSelected: null,
            isDisplayCode: false,
        }
    },
    methods: {
        async saveOverride() {
            this.isLoading = true;

            let body = [];
            for (const [, value] of Object.entries(this.bindings)) {
                body = [
                    ...body,
                    ...value
                        .filter(binding => !!binding.override)
                        .map(binding => ({
                            static_binding_id: binding.static_binding.id,
                            field: binding.override,
                        }))
                ]
            }

            try {
                await api.bindings.saveOverride(this.gatewayId, this.customerId, body);
                this.$toastr.s(this.$t('override.saveSuccess'));
            } catch (err) {
                this.$toastr.e(this.$t('override.error.save'));
            } finally {
                this.isLoading = false;
            }
        },
        async fetchData() {
            try {
                const data = await api.bindings.getOverride(this.gatewayId, this.customerId);
                this.gateway = data.gateway;
                this.customer = data.customer;
                this.bindings = regroupBindings(data.bindings);
                this.loadGatewayFiles();
            } catch (err) {
                this.$toastr.e(this.$t('override.error.loading'));
            }
        },
        async listingUpdated() {
            this.listing = await api.bindings.getListing(this.gatewayId, this.listingSelected);
        },
        getListingExampleValue(field, override) {
            if (!this.listing || !this.listing.content || !/^[a-zA-Z0-9_.]+$/.test(field) || !/^[a-zA-Z0-9_.]+$/.test(override)) {
                return null;
            }

            const valueOverride = override ? jsonpath.query(this.listing.content, `$..${override}`) : null;
            const valueDefault = field ? jsonpath.query(this.listing.content, `$..${field}`) : null;

            return override && valueOverride ? valueOverride[0]
                : override && !valueOverride ? null
                    : field && valueDefault ? valueDefault[0]
                        : field && !valueDefault ? null
                            : null;
        },
        loadGatewayFiles() {
            this.listingSelected = null;
            this.listing = {
                id: null,
                content: null,
                content_link: null,
            };
            this.gatewayFileSelected = null;
            this.gatewayFilesOptions = this.customer.gateway_files.map(gf => ({
                key: gf.id,
                text: gf.name,
                value: gf.id,
            }));
        },
        async gatewayFileUpdated() {
            this.listingSelected = null;
            this.listing = {
                id: null,
                content: null,
                content_link: null,
            };
            this.listingsOptions = [];
            if (this.gatewayFileSelected != null) {
                const data = await api.bindings.getListings(this.gatewayFileSelected);
                this.listingsOptions = data.map(listing => ({
                    key: listing.id,
                    text: '#' + listing.id,
                    value: listing.id,
                }));
            }
        },
        collapse(section) {
            document.getElementsByClassName(section).forEach((element) => element.classList.toggle('hidden'));
        },
        displayCode() {
            this.isDisplayCode = !this.isDisplayCode;
        }
    },
    async created() {
        this.isLoading = true;
        await this.fetchData();
        this.isLoading = false;
    },
}
</script>

<style>
.segment-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}
</style>
